/**
 * Credit Guard - Centralized credit charging wrapper
 * 
 * Ensures all AI operations charge credits atomically and idempotently
 * before executing the actual operation.
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { ServerFeatureKey } from '@/hooks/useCreditsSpend';

export interface CreditGuardResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  charged: boolean;
  remaining?: number;
}

/**
 * Execute an operation with automatic credit charging
 * @param featureKey - The server feature key for credit charging
 * @param refId - Idempotent reference ID
 * @param operation - The async operation to execute after charging
 * @param showToast - Whether to show toast notifications (default: true)
 */
export async function guardWithCredits<T>(
  featureKey: ServerFeatureKey,
  refId: string,
  operation: () => Promise<T>,
  showToast: boolean = true
): Promise<CreditGuardResult<T>> {
  try {
    // Phase 1: Charge credits (atomic, idempotent)
    const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
      body: { featureKey, refId }
    });

    if (creditError) {
      const errorData = creditError as any;
      
      // Handle premium-only features
      if (errorData.error === 'PREMIUM_REQUIRED') {
        if (showToast) {
          toast.error(errorData.message || 'This feature requires a Premium plan', {
            action: { label: 'Upgrade', onClick: () => window.location.href = '/pricing' },
          });
        }
        return { success: false, error: 'PREMIUM_REQUIRED', charged: false };
      }

      // Handle insufficient credits
      if (errorData.error === 'INSUFFICIENT_CREDITS') {
        if (showToast) {
          toast.error(errorData.message || 'Insufficient credits', {
            action: { label: 'Get Credits', onClick: () => window.location.href = '/pricing' },
          });
        }
        return { success: false, error: 'INSUFFICIENT_CREDITS', charged: false };
      }

      throw creditError;
    }

    // Phase 2: Execute the operation
    try {
      const result = await operation();
      
      // Show success toast if not idempotent
      if (showToast && !creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Used ${creditResult.cost} credit${creditResult.cost > 1 ? 's' : ''}. Remaining: ${creditResult.remaining}`);
      }
      
      return {
        success: true,
        data: result,
        charged: !creditResult.idempotent,
        remaining: creditResult.remaining
      };
    } catch (operationError) {
      // Operation failed after charging - log for admin review
      console.error('[CREDIT-GUARD] Operation failed after charging:', {
        featureKey,
        refId,
        error: operationError
      });
      
      if (showToast) {
        toast.error('Operation failed. Please contact support if credits were deducted.');
      }
      
      return {
        success: false,
        error: operationError instanceof Error ? operationError.message : 'Operation failed',
        charged: true // Credits were deducted
      };
    }
  } catch (error) {
    console.error('[CREDIT-GUARD] Unexpected error:', error);
    if (showToast) {
      toast.error('An unexpected error occurred. Please try again.');
    }
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unexpected error',
      charged: false
    };
  }
}

/**
 * Generate a hash for content-based refIds
 */
export async function hashContent(content: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(content);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
}

/**
 * Standard refId generators for consistency
 */
export const RefIdGenerator = {
  resume: (sectionId: string) => `resume:${sectionId}:${Date.now()}`,
  cover: (variant: string, jobHash: string) => `cover:${variant}:${jobHash}:${Date.now()}`,
  jobSearch: (queryHash: string, page: number) => `jobsearch:${queryHash}:p${page}`,
  jobAnalyzer: (jobHash: string) => `jobanalyze:${jobHash}:${Date.now()}`,
  ats: (resumeId: string, jobHash: string) => `ats:${resumeId}:${jobHash}:${Date.now()}`,
  linkedin: (profileId: string, timestamp?: number) => `linkedin:${profileId}:${timestamp || Date.now()}`,
  salaryMarket: (contextHash: string) => `salary:market:${contextHash}:${Date.now()}`,
  salaryOffer: (contextHash: string) => `salary:offer:${contextHash}:${Date.now()}`,
  salaryNegotiation: (contextHash: string) => `salary:negotiation:${contextHash}:${Date.now()}`,
  company: (domain: string) => `company:${domain}:${Date.now()}`,
  interviewQuick: (sessionId: string) => `interview:quick:${sessionId}`,
  interviewJob: (jobId: string, sessionId: string) => `interview:job:${jobId}:${sessionId}`,
  interviewCustom: (sessionId: string) => `interview:custom:${sessionId}`,
  smartApply: (jobId: string, queueId: string) => `smartapply:${jobId}:${queueId}`,
};
